import { PropsWithChildren, useCallback, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
} from '@mui/material';
import Modal from '~/components/Modal';
import { useHasAgreedToServiceTerms } from '~/hooks/local-storage/useHasAgreedToServiceTerms';
import { useTermsOfUseDocument } from '../../hooks/documents/useTermsOfUseDocument';
import { FormattedMessage } from 'react-intl';
import AppConfig from '~/utils/appconfig';
import TermsOfUseDocumentLink from './TermsOfUseDocumentLink';

/**
 * A React component that displays a modal dialog for users to agree to the terms of use.
 * This component ensures that users explicitly agree to the terms before proceeding.
 *
 * @remarks
 * - The modal is displayed if the `isReagreementNeeded` function returns `true` based on the `lastUpdated` timestamp.
 * - Users must check the agreement checkbox before confirming their acceptance of the terms.
 * - The modal includes a link to the terms of use document and displays the last updated date.
 */
export default function TermsOfUseModal({ children }: PropsWithChildren) {
    const { isReagreementNeeded, agreeToTerms } = useHasAgreedToServiceTerms();
    const { lastUpdated } = useTermsOfUseDocument();
    const [hasTentativelyAgreed, setHasTentativelyAgreed] = useState(false);

    const handleTentativelyAgreeToTerms = useCallback(() => {
        setHasTentativelyAgreed(!hasTentativelyAgreed);
    }, [hasTentativelyAgreed]);

    const handleConfirm = useCallback(() => {
        if (hasTentativelyAgreed) {
            agreeToTerms();
        } else {
            throw new Error('User must agree to terms before confirming.');
        }
    }, [agreeToTerms, hasTentativelyAgreed]);

    const isAgreementModalShown = Boolean(
        lastUpdated && isReagreementNeeded(lastUpdated)
    );

    return (
        <div>
            <Modal open={isAgreementModalShown} showCloseButton={false}>
                <Box display="grid" gap={2} minWidth={800} minHeight={325}>
                    <Box sx={{ mb: '0.5em' }}>
                        <Typography
                            id="modal-modal-title"
                            variant="h6"
                            component="h2"
                        >
                            <FormattedMessage id="termsOfUseDialog.title" />
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="body1">
                            <FormattedMessage
                                id={
                                    AppConfig.isDemo
                                        ? 'termsOfUseDialog.description.demo'
                                        : 'termsOfUseDialog.description'
                                }
                            />
                        </Typography>
                    </Box>

                    <TermsOfUseDocumentLink />

                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="nowrap"
                        gap={1}
                        mt={2}
                    >
                        <FormControlLabel
                            required
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={hasTentativelyAgreed}
                                    onChange={handleTentativelyAgreeToTerms}
                                />
                            }
                            label={
                                <FormattedMessage id="termsOfUseDialog.acceptTerms" />
                            }
                        />
                    </Box>

                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={handleConfirm}
                            disabled={!hasTentativelyAgreed}
                        >
                            <FormattedMessage id="termsOfUseDialog.confirm" />
                        </Button>
                    </Box>
                </Box>
            </Modal>
            {children}
        </div>
    );
}
